import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from './AuthContext';

const slugify = (v) =>
  (v || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-|-$/g, '');

const EMPTY = { slug: '', title: '', date: new Date().toISOString().slice(0, 10), tags: '', cover: '', cover_alt: '', excerpt: '', body: '', status: 'draft' };

export const AdminBlogEditor = ({ mode }) => {
  const { admin, authFetch } = useAuth();
  const { lang, slug } = useParams();
  const activeLang = lang || 'en';
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (mode !== 'edit') return;
    (async () => {
      try {
        const res = await authFetch(`/blog/${slug}`);
        if (!res.ok) throw new Error('Post not found');
        const data = await res.json();
        setForm({
          slug: data.slug || '',
          title: data.title || '',
          date: data.date || new Date().toISOString().slice(0, 10),
          tags: (data.tags || []).join(', '),
          cover: data.cover || '',
          cover_alt: data.cover_alt || '',
          excerpt: data.excerpt || '',
          body: data.body || '',
          status: data.status || 'draft',
        });
        setSlugTouched(true);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, [mode, slug]);

  if (admin === undefined) return <div className="min-h-screen bg-page" />;
  if (admin === null) return <Navigate to={`/${activeLang}/admin/login`} replace />;

  const update = (k) => (e) => {
    const val = e.target.value;
    setForm((p) => {
      const next = { ...p, [k]: val };
      if (k === 'title' && !slugTouched) next.slug = slugify(val);
      return next;
    });
  };

  const onSlugChange = (e) => {
    setSlugTouched(true);
    setForm((p) => ({ ...p, slug: slugify(e.target.value) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      slug: form.slug || slugify(form.title),
      title: form.title.trim(),
      date: form.date,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      cover: form.cover.trim(),
      cover_alt: (form.cover_alt || '').trim(),
      excerpt: form.excerpt.trim(),
      body: form.body,
      status: form.status || 'draft',
    };
    try {
      const isEdit = mode === 'edit';
      const path = isEdit ? `/blog/${slug}` : '/blog';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await authFetch(path, { method, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      navigate(`/${activeLang}/admin/blog`);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch('/upload/cover', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setForm((p) => ({ ...p, cover: data.url }));
    } catch (err) { setError(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  if (loading) return <main className="min-h-screen bg-page px-5 md:px-8 py-10"><p className="serif-italic text-ink/70">Loading…</p></main>;

  return (
    <main className="min-h-screen bg-page px-5 md:px-8 py-10" data-testid={`admin-editor-${mode}`}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ ADMIN</span>
            <span className="font-mono uppercase text-xs tracking-widest2 text-ink">
              {mode === 'edit' ? 'Edit post' : 'New post'}
            </span>
          </div>
          <Link to={`/${activeLang}/admin/blog`} className="font-mono uppercase text-xs tracking-widest text-ink link-underline">
            ← All posts
          </Link>
        </div>

        <h1 className="display text-5xl md:text-6xl leading-[0.9] text-ink mb-8">
          {mode === 'edit' ? 'Edit.' : 'Compose.'}
        </h1>

        <form onSubmit={onSubmit} className="grid grid-cols-12 gap-6 md:gap-8">
          {/* Left: form */}
          <div className="col-span-12 lg:col-span-6 flex flex-col gap-4">
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Title</label>
              <input type="text" required value={form.title} onChange={update('title')} placeholder="Post title" className="input-mono" data-testid="editor-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Slug (URL)</label>
                <input type="text" required value={form.slug} onChange={onSlugChange} placeholder="post-slug" className="input-mono" data-testid="editor-slug" />
              </div>
              <div>
                <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Date</label>
                <input type="date" required value={form.date} onChange={update('date')} className="input-mono" data-testid="editor-date" />
              </div>
            </div>
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Tags (comma-separated)</label>
              <input type="text" value={form.tags} onChange={update('tags')} placeholder="Marketing, Community, Founders" className="input-mono" data-testid="editor-tags" />
            </div>
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Cover image</label>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono uppercase text-[11px] tracking-widest text-ink cursor-pointer hover:bg-lime transition-colors" data-testid="editor-cover-upload">
                  <input type="file" accept="image/*" onChange={onCoverUpload} className="hidden" disabled={uploading} />
                  {uploading ? 'Uploading…' : 'Upload image'}
                  <span aria-hidden>↑</span>
                </label>
                {form.cover && (
                  <button type="button" onClick={() => setForm((p) => ({ ...p, cover: '' }))} className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60 link-underline">
                    Clear
                  </button>
                )}
              </div>
              <input type="url" value={form.cover} onChange={update('cover')} placeholder="…or paste an image URL" className="input-mono" data-testid="editor-cover" />
            </div>
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Cover alt text (for accessibility & SEO)</label>
              <input
                type="text"
                value={form.cover_alt}
                onChange={update('cover_alt')}
                placeholder="Describe what's in the cover image"
                className="input-mono"
                data-testid="editor-cover-alt"
                maxLength={200}
              />
              <p className="font-mono uppercase text-[9px] tracking-widest2 text-ink/50 mt-1">
                If empty, screen readers and SEO will fall back to the post title.
              </p>
            </div>
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5">Excerpt (list preview)</label>
              <textarea rows={2} value={form.excerpt} onChange={update('excerpt')} placeholder="Leave empty to auto-fill from the first sentence of the body" className="input-mono resize-none" data-testid="editor-excerpt" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70">Body (markdown)</label>
                <button type="button" onClick={() => setPreview((v) => !v)} className="font-mono uppercase text-[10px] tracking-widest2 text-ink link-underline" data-testid="editor-toggle-preview">
                  {preview ? 'Edit ↗' : 'Preview ↗'}
                </button>
              </div>
              {preview ? (
                <div className="box-line p-5 md:p-6 min-h-[240px]">
                  <div className="prose-zine">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || '*Nothing yet.*'}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <textarea
                  rows={16}
                  value={form.body}
                  onChange={update('body')}
                  placeholder={'Markdown supported. Use ## for headings, - for bullets, **bold**, *italic*, [link](url).'}
                  className="input-mono resize-y"
                  style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', textTransform: 'none' }}
                  data-testid="editor-body"
                />
              )}
            </div>

            {error && <p className="font-mono uppercase text-xs tracking-widest2 text-clay" role="alert" data-testid="editor-error">{error}</p>}

            {/* Status toggle */}
            <div className="box-line p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60 mb-1">Status</p>
                <p className="serif-italic text-sm text-ink/85">
                  {form.status === 'published' ? 'Published — visible on the public blog.' : 'Draft — hidden until you publish.'}
                </p>
              </div>
              <div className="flex items-center gap-2" role="group" aria-label="Post status">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, status: 'draft' }))}
                  className={`font-mono uppercase text-[11px] tracking-widest px-3 py-2 border border-ink transition-colors ${form.status === 'draft' ? 'bg-ink text-page' : 'bg-page text-ink hover:bg-yellow'}`}
                  data-testid="editor-status-draft"
                >
                  Draft
                </button>
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, status: 'published' }))}
                  className={`font-mono uppercase text-[11px] tracking-widest px-3 py-2 border border-ink transition-colors ${form.status === 'published' ? 'bg-lime text-ink' : 'bg-page text-ink hover:bg-lime'}`}
                  data-testid="editor-status-published"
                >
                  Published
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-ink text-page px-6 py-3.5 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-lime hover:text-ink transition-colors duration-300 disabled:opacity-60" data-testid="editor-save">
                {saving ? 'Saving…' : form.status === 'published' ? (mode === 'edit' ? 'Update & publish' : 'Publish post') : (mode === 'edit' ? 'Save draft' : 'Save as draft')}
                <span aria-hidden>→</span>
              </button>
              <Link to={`/${activeLang}/admin/blog`} className="font-mono uppercase text-xs tracking-widest text-ink link-underline">Cancel ↗</Link>
            </div>
          </div>

          {/* Right: live preview card as reader will see */}
          <aside className="col-span-12 lg:col-span-6">
            <p className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60 mb-3">— Live preview</p>
            <div className="box-line bg-page">
              {form.cover && (
                <div className="relative aspect-[4/3] overflow-hidden border-b border-ink">
                  <img src={form.cover} alt={form.cover_alt || form.title || ''} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5 md:p-6">
                <div className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60 mb-3">
                  {form.date} {form.tags && `· ${form.tags}`}
                </div>
                <h2 className="display text-3xl md:text-4xl leading-[0.95] text-ink mb-4">
                  {form.title || 'Post title'}
                </h2>
                <p className="serif-italic text-base text-ink/80 leading-snug mb-4">
                  {form.excerpt || 'Excerpt appears here.'}
                </p>
                <div className="prose-zine">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || '*Body preview.*'}</ReactMarkdown>
                </div>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </main>
  );
};
